import { lazy } from "react";
import { isPasswordResetLink, isVerifyEmailLink, readUrlSecret } from "./lib/urlSecrets";
import type { SignupIntent } from "./lib/signupIntent";

/**
 * ENG-3628 — the full-page views App renders instead of the sign-in screen or the console, which most
 * visits never show. Each is its own chunk, so the main bundle carries none of them.
 *
 * Every one is reached by the URL it was opened with: a password-reset or alias-confirmation link from
 * an email, the account-mcp "connect Claude" link, the extension's `?postupdate` tab, or a signup link
 * (`?source=` / `?plan=`), or a demo org claim link (`#claim=`, ENG-3832). So only the visitor who opened that link pays for the chunk, and
 * `prefetchRareViewsFor` starts it as App mounts, in parallel with Firebase's auth check rather than
 * after it.
 *
 * Not here, on purpose:
 * - `AuthScreen`: the signed-out first paint, and its largest element is the signed-out LCP.
 * - `AccountDeletedPage`: shown after a deletion inside a console that may have been open across a
 *   deploy. A stale chunk there would replace the deletion receipt with the "Haven has been updated"
 *   screen, to save about 2 KB.
 *
 * A rejected import throws past Suspense to the root ErrorBoundary (index.tsx), as DashboardLayout's does.
 */

const loadResetPasswordPage = () => import("./pages/ResetPasswordPage");
const loadVerifyEmailPage = () => import("./pages/VerifyEmailPage");
const loadConfirmAliasPage = () => import("./pages/ConfirmAliasPage");
const loadMcpAuthPage = () => import("./pages/McpAuthPage");
const loadWhatsNewPage = () => import("./pages/WhatsNewPage");
const loadSignupFlow = () => import("./components/onboarding/SignupFlow");
const loadClaimOrgFlow = () => import("./components/onboarding/ClaimOrgFlow");

export const ResetPasswordPage = lazy(() => loadResetPasswordPage().then((m) => ({ default: m.ResetPasswordPage })));
export const VerifyEmailPage = lazy(() => loadVerifyEmailPage().then((m) => ({ default: m.VerifyEmailPage })));
export const ConfirmAliasPage = lazy(() => loadConfirmAliasPage().then((m) => ({ default: m.ConfirmAliasPage })));
export const McpAuthPage = lazy(() => loadMcpAuthPage().then((m) => ({ default: m.McpAuthPage })));
export const WhatsNewPage = lazy(() => loadWhatsNewPage().then((m) => ({ default: m.WhatsNewPage })));
export const SignupFlow = lazy(() => loadSignupFlow().then((m) => ({ default: m.SignupFlow })));
export const ClaimOrgFlow = lazy(() => loadClaimOrgFlow().then((m) => ({ default: m.ClaimOrgFlow })));

/** What App was opened with that decides whether one of these views can show. */
export interface RareViewHints {
  /** A pending "connect Claude" request (useMcpAuthRequest). */
  mcpAuth: boolean;
  /** The signup link's intent (lib/signupIntent), or null. */
  signupIntent: SignupIntent | null;
  /** A demo org claim link this visit holds (lib/orgClaim, ENG-3832), from the URL or saved. */
  claimPending?: boolean;
}

/**
 * Start downloading the view this URL can lead to, so it is not one round trip behind auth. A failure
 * is ignored here: the lazy component asks again when it renders, and reports it then.
 */
export function prefetchRareViewsFor({ mcpAuth, signupIntent, claimPending }: RareViewHints): void {
  const quietly = (load: () => Promise<unknown>) => { load().catch(() => {}); };
  if (isPasswordResetLink()) quietly(loadResetPasswordPage);
  if (isVerifyEmailLink()) quietly(loadVerifyEmailPage);
  if (readUrlSecret("confirmAlias")) quietly(loadConfirmAliasPage);
  if (mcpAuth) quietly(loadMcpAuthPage);
  if (new URLSearchParams(window.location.search).has("postupdate")) quietly(loadWhatsNewPage);
  // `plan=individual` goes to the Chrome Web Store, never to SignupFlow.
  if (signupIntent && signupIntent.plan !== "individual") quietly(loadSignupFlow);
  if (claimPending) quietly(loadClaimOrgFlow);
}
